import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Stack,
  Chip,
  Paper,
  IconButton,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  useTheme,
  Tooltip
} from '@mui/material';
import {
  DndContext,
  DragOverlay,
  useSensor,
  useSensors,
  PointerSensor,
  KeyboardSensor,
  TouchSensor,
  rectIntersection,
  useDroppable,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';

// Icons
import RefreshIcon from '@mui/icons-material/Refresh';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import EditIcon from '@mui/icons-material/Edit';

// Colors
import { blue, green, orange, red, purple, grey, yellow } from '@mui/material/colors';

// Constants e Controllers
import { gFases, gFasesetapas } from '../../../constants/fases';
import { leadStatus } from '../../../constants/crm';
import { getMyLeads, encerrarLead } from '../../../controllers/crm/leadController';
import { setLeadTask } from '../../../controllers/crm/leadTaskController';

// Components
import LeadControleDetail from './LeadControleDetail';
import LeadEncerramento from './LeadEncerramento';

dayjs.locale('pt-br');

// Função para obter cor da fase
const getFaseColor = (faseNome, ordem) => {
  const colors = [blue[500], green[500], orange[500], purple[500], red[500], yellow[700]];
  return colors[ordem % colors.length] || grey[500];
};

// Componente do Card do Lead
const LeadCard = ({ lead, index, isDragging = false, onEditLead }) => {
  const theme = useTheme();
// console.log('Rendering LeadCard:', lead );
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging: sortableIsDragging,
  } = useSortable({
    id: `lead-${lead.idlead}`,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: sortableIsDragging ? 0.5 : 1,
  };

  const getStatusColor = () => {
    switch (lead.status) {
      case leadStatus.Designado:
        return green[100];
      case leadStatus.Encerrado:
        return grey[200];
      case leadStatus.Cancelado:
        return red[100];
      default:
        return blue[50];
    }
  };

  const getStatusTextColor = () => {
    switch (lead.status) {
      case leadStatus.Designado:
        return green[800];
      case leadStatus.Encerrado:
        return grey[700];
      case leadStatus.Cancelado:
        return red[800];
      default:
        return blue[800];
    }
  };

  return (
    <Card
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      elevation={sortableIsDragging ? 8 : 2}
      sx={{
        mb: 0.5,
        cursor: 'grab',
        bgcolor: getStatusColor(),
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 0,
        transition: 'all 0.2s ease-in-out',
        transform: sortableIsDragging ? 'rotate(5deg)' : 'rotate(0deg)',
        '&:hover': {
          transform: sortableIsDragging ? 'rotate(5deg)' : 'translateY(-2px)',
          boxShadow: sortableIsDragging ? 8 : 4
        },
        '&:active': {
          cursor: 'grabbing'
        }
      }}
    >
      <CardContent sx={{ p: 1, '&:last-child': { pb: 1 }, borderRadius: 0 }}>
        <Stack spacing={1}>
          {/* Header com nome e status */}
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
            <Typography
              variant="subtitle2"
              fontWeight="bold"
              sx={{
                flex: 1,
                color: getStatusTextColor(),
                fontSize: '0.85rem'
              }}
            >
              {lead.nome}
            </Typography>
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Tooltip title="Editar Lead">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onEditLead && onEditLead(lead);
                  }}
                  sx={{
                    width: 20,
                    height: 20,
                    color: 'primary.main',
                    '&:hover': {
                      bgcolor: 'primary.light',
                      color: 'white'
                    }
                  }}
                >
                  <EditIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
              <Chip
                size="small"
                label={lead.status === leadStatus.Designado ? 'Ativo' : 'Inativo'}
                sx={{
                  height: 20,
                  fontSize: '0.7rem',
                  bgcolor: lead.status === leadStatus.Designado ? green[200] : grey[300],
                  color: lead.status === leadStatus.Designado ? green[800] : grey[700]
                }}
              />
            </Stack>
          </Stack>

          {/* Informações de contato */}
          <Stack spacing={0.5}>
            {lead.telefone && (
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <PhoneIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                <Typography variant="caption" color="text.secondary">
                  {lead.telefone}
                </Typography>
              </Stack>
            )}
            {lead.email && (
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <EmailIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {lead.email}
                </Typography>
              </Stack>
            )}
          </Stack>

          {/* Data de criação */}
          {lead.createdat && (
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <CalendarTodayIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
              <Typography variant="caption" color="text.secondary">
                {dayjs(lead.createdat).format('DD/MM/YYYY HH:mm')}
              </Typography>
            </Stack>
          )}

          {/* Interesse */}
          {lead.interesse && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                fontStyle: 'italic',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {lead.interesse}
            </Typography>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};

// Componente para área de drop da fase
const FaseDroppable = ({ children, faseId }) => {
  const { isOver, setNodeRef } = useDroppable({
    id: `fase-${faseId}`,
  });

  return (
    <Box
      ref={setNodeRef}
      sx={{
        backgroundColor: isOver ? 'rgba(0, 0, 0, 0.05)' : 'transparent',
        transition: 'background-color 0.2s ease',
        flex: 1,
        minHeight: 0,
        width: '100%',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {children}
    </Box>
  );
};

// Componente da coluna da fase
const FaseColumn = ({ fase, leads, onRefresh, onEditLead }) => {
  const faseColor = getFaseColor(fase.fase, fase.ordem || 0);
  const leadIds = leads.map(lead => `lead-${lead.idlead}`);
  const encerrar = fase.idfase === process.env.REACT_APP_KEY_ARQUIVO;

  return (
    <Box
      sx={{
        flex: { xs: '0 0 85vw', sm: '0 0 45vw', md: '1 1 0' },
        display: 'flex',
        flexDirection: 'column',
        height: '100%', // Adicionar altura 100%
      }}
    >
      <Paper
        elevation={3}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 0,
          overflow: 'hidden',
          height: '100%', // Mudar de calc(100vh - 250px) para 100%
          minHeight: '450px',
          // Remover maxHeight ou ajustar conforme necessário
        }}
      >
        {/* Header da Coluna */}
        <Box
          sx={{
            bgcolor: faseColor,
            color: 'white',
            p: 1.5,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexShrink: 0,
          }}
        >
          <Stack sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle1" fontWeight="bold" noWrap>
              {encerrar ? 'ENCERRAR LEAD' : fase.fase}
            </Typography>

            <Typography variant="caption" sx={{ opacity: 0.9 }} noWrap>
              {!encerrar && `${leads.length} lead${leads.length !== 1 ? 's' : ''}`}
              {encerrar && 'Arraste aqui para encerrar'}
            </Typography>
          </Stack>
          <Tooltip title="Atualizar">
            <IconButton
              size="small"
              onClick={onRefresh}
              sx={{ color: 'white', flexShrink: 0 }}
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Área de Drop */}
        <FaseDroppable faseId={fase.idfase}>
          <SortableContext items={leadIds} strategy={verticalListSortingStrategy}>
            <Box
              sx={{
                flex: 1,
                p: 1,
                bgcolor: 'background.paper',
                transition: 'background-color 0.2s ease',
                overflowY: 'auto',
                overflowX: 'hidden',
                minHeight: 0,
                height: '100%',
                '&::-webkit-scrollbar': {
                  width: '6px',
                },
                '&::-webkit-scrollbar-track': {
                  background: grey[100],
                  borderRadius: '0px',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: grey[400],
                  borderRadius: '0px',
                  '&:hover': {
                    background: grey[600],
                  },
                },
              }}
            >
              {leads.map((lead, index) => (
                <LeadCard
                  key={lead.idlead}
                  lead={lead}
                  index={index}
                  onEditLead={onEditLead}
                />
              ))}

              {/* Placeholder quando vazio */}
              {leads.length === 0 && (
                <Box
                  sx={{
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'text.secondary',
                    textAlign: 'center',
                    minHeight: '200px',
                    p: 2,
                  }}
                >
                  <Typography variant="body2">
                    Arraste leads aqui
                  </Typography>
                </Box>
              )}
            </Box>
          </SortableContext>
        </FaseDroppable>
      </Paper>
    </Box>
  );
};

// Componente Principal
export default function LeadsControle({
  account,
  setSnackbar,
  setUpdate,
  update
}) {
  // Estados
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeId, setActiveId] = useState(null);
  const [transferDialog, setTransferDialog] = useState({
    open: false,
    lead: null,
    sourceFase: null,
    targetFase: null
  });
  const [showLeadDetail, setShowLeadDetail] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);

  // Estados para encerramento de leads
  const [showEncerrarLead, setShowEncerrarLead] = useState(false);
  const [leadToEncerrar, setLeadToEncerrar] = useState(null);
  const [motivoEncerramento, setMotivoEncerramento] = useState('');
  const [observacaoEncerramento, setObservacaoEncerramento] = useState('');
  const [loadingEncerrar, setLoadingEncerrar] = useState(false);

  // Sensores do DnD Kit
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 3,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 100,
        tolerance: 5,
      },
    })
  );

  // Carregar leads
  const loadLeads = useCallback(async () => {
    try {
      setLoading(true);
      const result = await getMyLeads(account);

      if (result.status === 200) {
        setLeads(result.data || []);
      } else {
        setSnackbar({
          severity: 'error',
          message: 'Erro ao carregar leads'
        });
      }
    } catch (error) {
      console.error('Erro ao carregar leads:', error);
      setSnackbar({
        severity: 'error',
        message: 'Erro ao carregar leads'
      });
    } finally {
      setLoading(false);
    }
  }, [account, setSnackbar]);

  // Agrupar leads por fase
  const leadsByFase = useMemo(() => {
    const grouped = {};

    // Inicializar todas as fases
    gFases.forEach(fase => {
      grouped[fase.idfase] = [];
    });

    // Agrupar leads por fase
    leads.forEach(lead => {
      const faseId = lead.idfase || 'sem-fase';
      if (grouped[faseId]) {
        grouped[faseId].push(lead);
      }
    });

    return grouped;
  }, [leads]);

  // Handler para início do drag
  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  // Handler para drag and drop com @dnd-kit
  const handleDragEnd = (event) => {
    const { active, over } = event;

    // console.log('Drag end event:', { 
    //   activeId: active.id, 
    //   overId: over?.id,
    //   overRect: over?.rect 
    // });

    // Se não foi dropado em lugar válido
    if (!over) {
      // console.log('No drop target, drag cancelled');
      setActiveId(null);
      return;
    }

    // Se foi dropado no mesmo lugar
    if (active.id === over.id) {
      // console.log('Same position, no change needed');
      setActiveId(null);
      return;
    }

    // Extrair IDs dos prefixos
    const leadId = active.id.replace('lead-', '');
    const targetId = over.id;

    // console.log('Lead ID:', leadId, 'Target:', targetId);

    // Encontrar o lead que foi movido
    const lead = leads.find(l => l.idlead === leadId);
    if (!lead) {
      // console.log('Lead not found:', leadId);
      setActiveId(null);
      return;
    }

    // Determinar a fase de destino
    let targetFaseId = null;

    // Se foi dropado em outro lead, usar a fase desse lead
    if (targetId.startsWith('lead-')) {
      const targetLeadId = targetId.replace('lead-', '');
      const targetLead = leads.find(l => l.idlead === targetLeadId);
      if (targetLead) {
        targetFaseId = targetLead.idfase;
        // console.log('Dropped on lead, target phase:', targetFaseId);
      }
    }
    // Se foi dropado em uma fase (container), usar essa fase
    else if (targetId.startsWith('fase-')) {
      targetFaseId = targetId.replace('fase-', '');
      // console.log('Dropped on phase container:', targetFaseId);
    }

    if (!targetFaseId) {
      // console.log('Could not determine target phase');
      setActiveId(null);
      return;
    }

    const sourceFaseId = lead.idfase;

    // Se moveu para a mesma fase, apenas reordenar
    if (sourceFaseId === targetFaseId) {
      // console.log('Reordering within same phase');
      setActiveId(null);
      return;
    }

    // Se moveu para fase diferente, mostrar diálogo de confirmação
    const sourceFase = gFases.find(f => f.idfase === sourceFaseId);
    const targetFase = gFases.find(f => f.idfase === targetFaseId);

    // console.log('Moving to different phase:', { lead, sourceFase, targetFase });

    if (lead && sourceFase && targetFase) {
      setTransferDialog({
        open: true,
        lead,
        sourceFase,
        targetFase
      });
    } else {
      // console.log('Missing data for transfer:', {
      //   leadFound: !!lead,
      //   sourceFaseFound: !!sourceFase,
      //   targetFaseFound: !!targetFase
      // });
    }

    setActiveId(null);
  };

  // Confirmar transferência de lead
  const handleConfirmTransfer = async () => {
    const { lead, targetFase } = transferDialog;
    // console.log('Confirming transfer for lead:', lead, 'to phase:', targetFase);

    try {
      setLoading(true);
      // Encontrar a etapa com a menor ordemt para a fase selecionada
      const etapasDaFase = gFasesetapas.filter(etapa => etapa.idfase === targetFase.idfase);
      const etapa = etapasDaFase.length > 0
        ? etapasDaFase.reduce((prev, curr) => (prev.ordemt < curr.ordemt ? prev : curr))
        : null;
      const taskData = {
        idacao: null,
        version: 1,
        versionLead: lead.version,
        idoperador: account.id,
        idconta: account.conta,
        idlead: lead.idlead,
        idfase: targetFase.idfase,
        idetapa: etapa.idetapa,
        tipo: 'ta', // Tarefa
        prioridade: 'n', // Normal
        ord: 2,
        titulo: etapa.etapa, // 
        descricao: `Lead transferido para a fase ${targetFase.fase}`,
        situacao: 'co', // Concluída
        dtacao: dayjs().toISOString(),
      };

      if (targetFase.idfase === process.env.REACT_APP_KEY_ARQUIVO) {
        // Usar componente específico para arquivamento/encerramento
        handleCancelTransfer();
        handleOpenEncerrarLead(lead);
        return;
      }

      const response = await setLeadTask(taskData);

      if (response.status === 200) {
        // Atualizar lead local
        setLeads(prevLeads =>
          prevLeads.map(l =>
            l.idlead === lead.idlead
              ? { ...l, idfase: targetFase.idfase, version: response.data.lead.version }
              : l
          )
        );

        setSnackbar({
          severity: 'success',
          message: `Lead movido para ${targetFase.fase} com sucesso`
        });

        setUpdate && setUpdate(Date.now());
      } else {
        throw new Error('Erro na resposta do servidor');
      }
    } catch (error) {
      console.error('Erro ao transferir lead:', error);
      setSnackbar({
        severity: 'error',
        message: 'Erro ao mover lead. Tente novamente.'
      });
    } finally {
      setLoading(false);
      setTransferDialog({ open: false, lead: null, sourceFase: null, targetFase: null });
    }
  };

  // Cancelar transferência
  const handleCancelTransfer = () => {
    setTransferDialog({ open: false, lead: null, sourceFase: null, targetFase: null });
  };

  // Abrir detalhe do lead para edição
  const handleEditLead = (lead) => {
    setSelectedLead(lead);
    setShowLeadDetail(true);
  };

  // Fechar detalhe do lead
  const handleCloseLeadDetail = () => {
    setShowLeadDetail(false);
    setSelectedLead(null);
  };

  // Funções para encerramento de leads
  const handleOpenEncerrarLead = (lead) => {
    setLeadToEncerrar(lead);
    setShowEncerrarLead(true);
    setMotivoEncerramento('');
    setObservacaoEncerramento('');
  };

  const handleCloseEncerrarLead = () => {
    setShowEncerrarLead(false);
    setLeadToEncerrar(null);
    setMotivoEncerramento('');
    setObservacaoEncerramento('');
  };

  const handleEncerrarLead = async () => {
    if (!leadToEncerrar || !motivoEncerramento) {
      setSnackbar({
        severity: 'warning',
        message: 'Selecione um motivo para o encerramento'
      });
      return;
    }

    try {
      setLoadingEncerrar(true);

      const encerramentoData = {
        idlead: leadToEncerrar.idlead,
        motivo: motivoEncerramento,
        observacao: observacaoEncerramento,
        idoperador: account.id,
        idconta: account.conta,
        version: leadToEncerrar.version
      };

      const response = await encerrarLead(encerramentoData);

      if (response.status === 200) {
        // Remover lead da lista local (ou atualizar para fase de arquivo)
        setLeads(prevLeads =>
          prevLeads.filter(l => l.idlead !== leadToEncerrar.idlead)
        );

        setSnackbar({
          severity: 'success',
          message: 'Lead encerrado com sucesso'
        });

        setUpdate && setUpdate(Date.now());
        handleCloseEncerrarLead();
      } else {
        throw new Error('Erro na resposta do servidor');
      }
    } catch (error) {
      console.error('Erro ao encerrar lead:', error);
      setSnackbar({
        severity: 'error',
        message: 'Erro ao encerrar lead. Tente novamente.'
      });
    } finally {
      setLoadingEncerrar(false);
    }
  };

  // Carregar dados ao montar o componente
  useEffect(() => {
    loadLeads();
  }, [update, loadLeads]);

  // Debug: verificar se gFases está carregado
  useEffect(() => {
    // console.log('gFases:', gFases);
    // console.log('gFasesetapas:', gFasesetapas);
    // console.log('leads:', leads);
    // console.log('leadsByFase:', leadsByFase);
  }, [leads, leadsByFase]);

  if (loading && leads.length === 0) {
    return (
      <Box
        sx={{
          height: 'calc(100vh - 200px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Verificar se gFases está carregado antes de renderizar o DragDropContext
  if (!gFases || gFases.length === 0) {
    return (
      <Box
        sx={{
          height: 'calc(100vh - 200px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column'
        }}
      >
        <CircularProgress />
        <Typography variant="body2" sx={{ mt: 2 }}>
          Carregando fases...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      height: '90dvh',
      overflow: 'hidden',
      boxSizing: 'border-box',
    }}>
      {/* Header */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ px: 2, flexShrink: 0 }} // Adicionado padding horizontal
      >
        <Typography variant="h4" fontWeight="bold" color="primary">
          Controle de Leads
        </Typography>
        <Stack direction="row" spacing={1}>
          <Tooltip title="Atualizar todos">
            <IconButton onClick={loadLeads} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      {/* Estatísticas rápidas */}
      <Grid container spacing={2} sx={{ mb: 1, px: 2, flexShrink: 0, justifyContent: "space-between" }}>
        <Grid item xs={6} md={3}>
          <Paper sx={{ p: 2, textAlign: 'center', borderRadius: 0 }}>
            <Typography variant="h4" color="primary" fontWeight="bold">
              {leads.length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total de Leads
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} md={3}>
          <Paper sx={{ p: 2, textAlign: 'center', borderRadius: 0 }}>
            <Typography variant="h4" color="info.main" fontWeight="bold">
              {leads.filter(l => l.dtdesignacao && dayjs(l.dtdesignacao).isAfter(dayjs().startOf('month'))).length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Este Mês
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Kanban Board */}
      <Box sx={{ flex: 1, overflow: 'hidden', px: 2 }}>
        <DndContext
          sensors={sensors}
          collisionDetection={rectIntersection}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <Box
            sx={{
              height: '100%',
              overflowX: 'auto',
              overflowY: 'hidden',
              pb: 2,
              '&::-webkit-scrollbar': {
                height: '12px',
              },
              '&::-webkit-scrollbar-track': {
                background: grey[100],
              },
              '&::-webkit-scrollbar-thumb': {
                background: grey[400],
                borderRadius: '0px',
                '&:hover': {
                  background: grey[600],
                },
              },
            }}
          >
            <Stack
              direction="row"
              spacing={1.5}
              sx={{
                height: '100%', // Garantir altura 100%
                display: 'flex',
                flexWrap: 'nowrap',
                pb: 1,
                minWidth: { xs: 'auto', md: '100%' },
              }}
            >
              {gFases
                .sort((a, b) => (a.ordem || 0) - (b.ordem || 0))
                .map(fase => {
                  if (!fase.idfase) {
                    return null;
                  }

                  return (
                    <FaseColumn
                      key={fase.idfase}
                      fase={fase}
                      leads={(leadsByFase[fase.idfase] || []).map((lead, index) => ({
                        ...lead,
                        uniqueKey: `${lead.idlead}-${fase.idfase}`
                      }))}
                      onRefresh={loadLeads}
                      onEditLead={handleEditLead}
                    />
                  );
                })}
            </Stack>
          </Box>

          {/* Overlay para drag visual */}
          <DragOverlay>
            {activeId ? (
              <LeadCard
                lead={leads.find(l => `lead-${l.idlead}` === activeId)}
                index={-1}
                isDragging={true}
                onEditLead={handleEditLead}
              />
            ) : null}
          </DragOverlay>
        </DndContext>
      </Box>

      {/* Dialog de Confirmação */}
      <Dialog
        open={transferDialog.open}
        onClose={handleCancelTransfer}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            fontSize: '1.125rem', // 
            fontWeight: 'bold'
          }}
        >
          Confirmar Transferência
        </DialogTitle>
        <DialogContent>
          <Typography>
            Deseja mover o lead <strong>{transferDialog.lead?.nome}</strong> da fase{' '}
            <strong>{transferDialog.sourceFase?.fase}</strong> para{' '}
            <strong>{transferDialog.targetFase?.fase}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Esta ação criará uma nova tarefa registrando a mudança de fase.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelTransfer}>
            Cancelar
          </Button>
          <Button
            onClick={handleConfirmTransfer}
            variant="contained"
            disabled={loading}
          >
            {loading ? <CircularProgress size={20} /> : 'Confirmar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Loading overlay */}
      {loading && leads.length > 0 && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: 'rgba(255, 255, 255, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999
          }}
        >
          <CircularProgress />
        </Box>
      )}

      {/* Lead Detail Modal */}
      {showLeadDetail && selectedLead && (
        <Dialog
          open={showLeadDetail}
          onClose={handleCloseLeadDetail}
          fullScreen={window.innerWidth < 600}
        // maxWidth="lg"

        // fullWidth
        // PaperProps={{
        //   sx: {
        //     height: '90vh',
        //     maxHeight: '90vh',
        //     m: 2,
        //     borderRadius: 2,
        //     overflow: 'hidden'
        //   }
        // }}
        >
          <LeadControleDetail
            account={account}
            lead={selectedLead}
            setShowLeadControle={handleCloseLeadDetail}
            setSnackbar={setSnackbar}
            setUpdate={setUpdate}
            update={update}
          />
        </Dialog>
      )}

      {/* Modal de Encerramento de Lead */}
      <LeadEncerramento
        lead={leadToEncerrar}
        showEncerrarLead={showEncerrarLead}
        handleCloseEncerrarLead={handleCloseEncerrarLead}
        handleEncerrarLead={handleEncerrarLead}
        motivoEncerramento={motivoEncerramento}
        setMotivoEncerramento={setMotivoEncerramento}
        observacaoEncerramento={observacaoEncerramento}
        setObservacaoEncerramento={setObservacaoEncerramento}
        loadingEncerrar={loadingEncerrar}
      />
    </Box>
  );
}
